import { Card, Empty, List, Pagination, Spin, Typography } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { ApplicationCard } from 'components/molecules/ApplicationCard';
import { useTheme } from 'context/ThemeContext';
import { memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetApplicationsQuery } from 'redux/services/freddyCadabby/freddyCadabbyApi';
import { ReduxState, useAppSelector } from 'redux/store';
import { useRenderPerformanceMonitor } from 'utils/performanceUtils';

// Constants for pagination
const PAGE_SIZE = 12;

export const WarehouseApplicationsPage = memo((): JSX.Element => {
  useRenderPerformanceMonitor('WarehouseApplicationsPage');
  const { navSearchValue: searchValue } = useAppSelector((state) => state.businessList);

  const { includeInactiveData } = useSelector((state: ReduxState) => state.freddyCadabbyParams);
  const { isDarkMode } = useTheme();
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, isFetching } = useGetApplicationsQuery({
    isDivisionContextRequired: false,
    includeInactiveData
  });

  const { xs, sm, md } = useBreakpoint();
  const isMobile = xs || sm;

  const filteredApplications = useMemo(() => {
    if (!data?.data) return [];

    let filtered = data.data.filter((app) => app.menuTab === 'Warehouse');

    if (xs) {
      filtered = filtered.filter((app) => app.isMobileOptimized);
    }

    if (searchValue) {
      filtered = filtered.filter((app) => app.name.toLowerCase().includes(searchValue.toLowerCase()));
    }
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [data?.data, searchValue, xs]);

  // Paginate the data
  const paginatedApplications = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredApplications.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredApplications, currentPage]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="warehouse-applications-wrapper" style={{ maxWidth: 1920 }}>
      <Card
        className="warehouse-applications-container"
        bordered={false}
        style={{
          borderRadius: 8,
          padding: '0px 16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          background: isDarkMode ? 'rgba(57, 56, 60, 0.51)' : 'rgb(229, 232, 238)',
          opacity: isDarkMode ? 0.8 : 1,
          border: isDarkMode ? '1px solid rgb(69, 68, 76)' : '1px solid #e8e8e8'
        }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
            <Spin size="large" tip="Loading applications..." />
          </div>
        ) : (
          <>
            {paginatedApplications.length === 0 ? (
              <Empty
                description={
                  <Typography.Text type="secondary" style={{ fontSize: 16, color: isDarkMode ? '#fff' : '#000' }}>
                    {searchValue ? 'No applications match your search' : 'No applications available'}
                  </Typography.Text>
                }
                style={{
                  padding: '40px 0',
                  background: isDarkMode ? 'rgba(57, 56, 60, 0.51)' : '#f5f5f5',
                  borderRadius: 8
                }}
              />
            ) : (
              <div style={{ position: 'relative' }}>
                {isFetching && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(255, 255, 255, 0.6)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      zIndex: 1,
                      borderRadius: 8
                    }}>
                    <Spin />
                  </div>
                )}
                <List
                  grid={{
                    column: 1,
                    gutter: 24,
                    xs: 1,
                    sm: 2,
                    md: 3,
                    lg: 3,
                    xl: 3,
                    xxl: 3
                  }}
                  dataSource={paginatedApplications}
                  renderItem={(item): JSX.Element => (
                    <List.Item style={{ marginBottom: 24, height: '100%' }}>
                      <ApplicationCard key={item.id} application={item} />
                    </List.Item>
                  )}
                  style={{
                    marginTop: 8,
                    background: 'transparent',
                    borderRadius: 8,
                    width: '100%'
                  }}
                />
              </div>
            )}
          </>
        )}

        {filteredApplications.length > PAGE_SIZE && (
          <Pagination
            current={currentPage}
            onChange={handlePageChange}
            total={filteredApplications.length}
            pageSize={PAGE_SIZE}
            showSizeChanger={false}
            style={{
              marginTop: 24,
              textAlign: 'center',
              padding: '8px 0'
            }}
            showTotal={(total) => `Total ${total} items`}
            responsive
          />
        )}
      </Card>
    </div>
  );
});
